import { z } from 'zod'
import type { AccountType, JournalKind } from './ledger.js'

/**
 * Financial reports (S6). All of them are read-only queries over the general ledger and the posted
 * documents; dates are business dates (YYYY-MM-DD, Asia/Muscat), inclusive at both ends.
 */

const RANGE_MESSAGE = 'The end date must be after the start date'

/** Longest period an operational or cash-flow report may cover. */
export const MAX_REPORT_YEARS = 5

/** True when `to` lies `years` whole years or more after `from` (both YYYY-MM-DD). */
export function spanExceedsYears(from: string, to: string, years: number): boolean {
  const [y, m, d] = from.split('-').map(Number) as [number, number, number]
  // Date.UTC rolls 29 February over to 1 March in a common year, which is the right exclusive limit.
  const limit = new Date(Date.UTC(y + years, m - 1, d))
  return new Date(`${to}T00:00:00Z`) >= limit
}

/**
 * Validation issues of a report period: `to` not before `from`, and at most MAX_REPORT_YEARS long
 * (from 2021-01-01 the last allowed end date is 2025-12-31).
 */
export function reportPeriodIssues(from: string, to: string): { path: string[]; message: string }[] {
  if (to < from) return [{ path: ['to'], message: RANGE_MESSAGE }]
  if (spanExceedsYears(from, to, MAX_REPORT_YEARS)) {
    return [{ path: ['to'], message: `A report covers at most ${MAX_REPORT_YEARS} years` }]
  }
  return []
}

export const profitAndLossQuerySchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    /** Optional comparison period (both dates or neither). */
    compareFrom: z.iso.date().optional(),
    compareTo: z.iso.date().optional(),
  })
  .superRefine((q, ctx) => {
    if (q.to < q.from) ctx.addIssue({ code: 'custom', path: ['to'], message: RANGE_MESSAGE })
    if ((q.compareFrom === undefined) !== (q.compareTo === undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: [q.compareFrom === undefined ? 'compareFrom' : 'compareTo'],
        message: 'Give both dates of the comparison period',
      })
    } else if (q.compareFrom && q.compareTo && q.compareTo < q.compareFrom) {
      ctx.addIssue({ code: 'custom', path: ['compareTo'], message: RANGE_MESSAGE })
    }
  })
export type ProfitAndLossQuery = z.input<typeof profitAndLossQuerySchema>

export const balanceSheetQuerySchema = z.object({ asOf: z.iso.date() })
export type BalanceSheetQuery = z.input<typeof balanceSheetQuerySchema>

export const accountStatementQuerySchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    /** Narrows the statement to one sub-ledger party (both fields or neither). */
    partyType: z.enum(['CUSTOMER', 'SUPPLIER']).optional(),
    partyId: z.uuid().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(100),
  })
  .superRefine((q, ctx) => {
    if (q.to < q.from) ctx.addIssue({ code: 'custom', path: ['to'], message: RANGE_MESSAGE })
    if ((q.partyType === undefined) !== (q.partyId === undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: [q.partyType === undefined ? 'partyType' : 'partyId'],
        message: 'Give both the party type and the party',
      })
    }
  })
export type AccountStatementQuery = z.input<typeof accountStatementQuerySchema>

export const vatReturnQuerySchema = z
  .object({ from: z.iso.date(), to: z.iso.date() })
  .superRefine((q, ctx) => {
    if (q.to < q.from) ctx.addIssue({ code: 'custom', path: ['to'], message: RANGE_MESSAGE })
  })
export type VatReturnQuery = z.input<typeof vatReturnQuerySchema>

/**
 * One account in a report section, laid out as the chart: depth-first in code order, header accounts
 * first and carrying the sum of their descendants. Amounts are on the section's natural side
 * (revenue, liabilities, equity: credit − debit; expenses, assets: debit − credit), so a contra
 * account shows negative.
 */
export interface ReportAccountRow {
  accountId: string
  code: string
  name: string
  nameAr: string | null
  /** 0 for a top-level account. */
  depth: number
  isPostable: boolean
  amount: string
  /** The comparison period's amount; null when no comparison was asked for. */
  compareAmount: string | null
}

export interface ProfitAndLossTotals {
  totalRevenue: string
  totalExpenses: string
  netProfit: string
}

/** Movement of income and expense accounts over a period, excluding year-end closing entries. */
export interface ProfitAndLoss extends ProfitAndLossTotals {
  from: string
  to: string
  revenue: ReportAccountRow[]
  expenses: ReportAccountRow[]
  /** Totals of the comparison period; null without one. */
  compare: (ProfitAndLossTotals & { from: string; to: string }) | null
}

/** Equity lines the balance sheet computes from income and expense accounts (no account of their own). */
export const EARNINGS_LINES = {
  CURRENT_YEAR_EARNINGS: { en: 'Current year earnings', ar: 'أرباح السنة الحالية' },
  PRIOR_YEARS_EARNINGS: { en: 'Undistributed earnings of prior years', ar: 'أرباح سنوات سابقة غير موزعة' },
} as const
export type EarningsLineKey = keyof typeof EARNINGS_LINES

export interface EarningsLine {
  key: EarningsLineKey
  name: string
  nameAr: string
  amount: string
}

export interface BalanceSheet {
  asOf: string
  /** Start of the fiscal year containing `asOf` (1 January when no fiscal year covers it). */
  fiscalYearStart: string
  assets: ReportAccountRow[]
  liabilities: ReportAccountRow[]
  equity: ReportAccountRow[]
  /**
   * Part of equity: the current fiscal year's result to `asOf`, and income and expense balances of
   * earlier years that were never closed into retained earnings. Both lines are always present.
   */
  earnings: EarningsLine[]
  totalAssets: string
  totalLiabilities: string
  /** Equity accounts plus the earnings lines. */
  totalEquity: string
  totalLiabilitiesAndEquity: string
  /** Assets = liabilities + equity, to the baisa. */
  balanced: boolean
}

export interface AccountStatementLine {
  date: string
  journalEntryId: string
  journalNumber: string
  kind: JournalKind
  sourceType: string | null
  sourceId: string | null
  /** The journal entry's description. */
  description: string | null
  lineDescription: string | null
  debit: string
  credit: string
  /** Running balance after this line, on the account's natural side. */
  balance: string
  party: { type: 'CUSTOMER' | 'SUPPLIER'; id: string; code: string | null; name: string | null } | null
}

/** General ledger of one postable account. Balances are on the account's natural side. */
export interface AccountStatement {
  account: {
    id: string
    code: string
    name: string
    nameAr: string | null
    type: AccountType
    normalSide: 'DEBIT' | 'CREDIT'
  }
  from: string
  to: string
  party: { type: 'CUSTOMER' | 'SUPPLIER'; id: string } | null
  /** Balance before `from`. */
  opening: string
  periodDebit: string
  periodCredit: string
  /** Balance at the end of `to`. */
  closing: string
  /** Balance brought forward to the first line of this page (the opening balance on page 1). */
  pageOpening: string
  lines: AccountStatementLine[]
  page: number
  pageSize: number
  /** Number of lines in the period. */
  total: number
}

export interface VatRateRow {
  /** The return box: standard-rated, zero-rated, exempt or out of scope (no tax rate). */
  category: 'STANDARD' | 'ZERO' | 'EXEMPT' | 'OUT_OF_SCOPE'
  /** Percent, e.g. "5.00". */
  rate: string
  net: string
  tax: string
}

export interface VatSection {
  rows: VatRateRow[]
  net: string
  tax: string
}

/** One supplier's input VAT on the return (G18). */
export interface VatSupplierRow {
  /**
   * Null: the expenses with VAT that name no supplier. The VATIN is read live from the supplier. The API
   * always sends `nameAr`; it is optional in the type only for older fixtures.
   */
  supplier: {
    id: string
    code: string
    name: string
    nameAr?: string | null
    taxNumber: string | null
  } | null
  /** Documents counted: purchases, supplier returns and expenses. */
  documents: number
  net: string
  tax: string
  /** No VATIN on the supplier (or no supplier at all): its input VAT may not be reclaimable. */
  missingVatin: boolean
}

export interface VatLedgerAccount {
  id: string
  code: string
  name: string
  nameAr: string | null
}

/**
 * Oman VAT return for a period (tax point = document date), from the documents' own snapshots, with
 * the ledger movement on the VAT accounts alongside so the two can be reconciled.
 */
export interface VatReturn {
  from: string
  to: string
  /** Supplies: posted invoices and debit notes, by rate; the zero-rated row is always present. */
  output: VatSection
  /** Purchases: posted expenses recorded with a tax rate, by rate. */
  input: VatSection
  /**
   * G18: the input section by supplier, from the same documents, dates and basis (posted purchases less
   * posted supplier returns, plus expenses naming a supplier), with one `supplier: null` row for the
   * expenses with VAT that name none. Its `tax` sums to `input.tax`. Optional in the type for fixtures.
   */
  inputBySupplier?: VatSupplierRow[]
  /** Output tax − input tax. */
  netPayable: string
  /**
   * What the documents posted to the VAT accounts in the period (their entries and voids), compared with
   * the documents themselves.
   */
  ledger: {
    outputVat: { account: VatLedgerAccount | null; amount: string }
    inputVat: { account: VatLedgerAccount | null; amount: string }
    netPayable: string
    /**
     * AA1 accounting review: the other movements on the VAT accounts in the period (manual journals: paying
     * or settling VAT, corrections, opening balances, and their reversals), apart. They never make a
     * difference. Optional for fixtures.
     */
    manual?: { outputVat: string; inputVat: string; netPayable: string }
  }
  /**
   * Document-posted ledger − documents. Non-zero only when the documents' own postings disagree with them
   * (an account re-mapped, data repaired by hand); shown, never hidden.
   */
  difference: { outputTax: string; inputTax: string; netPayable: string }
}

export const cashFlowQuerySchema = z
  .object({ from: z.iso.date(), to: z.iso.date() })
  .superRefine((q, ctx) => {
    for (const issue of reportPeriodIssues(q.from, q.to)) ctx.addIssue({ code: 'custom', ...issue })
  })
export type CashFlowQuery = z.input<typeof cashFlowQuerySchema>

/** One counter-account's share of the period's cash movement (inflow positive, outflow negative). */
export interface CashFlowLine {
  accountId: string
  code: string
  name: string
  nameAr: string | null
  amount: string
}

export interface CashFlowSection {
  /** By counter-account, in chart order; accounts whose share nets to zero are left out. */
  lines: CashFlowLine[]
  total: string
}

/**
 * Cash flow statement, direct method, from the ledger. Cash is the GL accounts behind the treasury
 * (cash and bank) accounts. Each entry of the period that changes cash has that change spread over
 * its other lines by their signed amounts, and each line is classified by its account:
 *   operating — receivables, payables, revenue, expense, VAT, inventory and customer-credit accounts;
 *   investing — other asset accounts (fixed assets and the like);
 *   financing — equity and other liability accounts (loans, owner capital).
 * Entries that only move money between treasury accounts change nothing and are left out.
 * openingCash + netChange = closingCash, to the baisa.
 */
export interface CashFlowStatement {
  from: string
  to: string
  /** Σ treasury GL balances before `from`. */
  openingCash: string
  operating: CashFlowSection
  investing: CashFlowSection
  financing: CashFlowSection
  /** operating + investing + financing. */
  netChange: string
  /** Σ treasury GL balances at the end of `to`. */
  closingCash: string
}
